import {OrderManager} from '@nuskin/ns-shop';
import {ProfileService, UserService} from '@nuskin/ns-account';
import {getCachedConfigField} from '@nuskin/configuration-sdk';

const DropShipUtil = function() {
    'use strict';

    let $scope;
    let maxJaroScore;
    let nameMaxJaroScore;

    // ---------------------------------------------
    //
    //        Public Methods
    //
    // ---------------------------------------------
    const publicMethods = {
        initDropShip: function(scope, simulatePromiseDependencies) {
            $scope = scope;
            $scope.showDropShip = false;
            maxJaroScore = getCachedConfigField('dropShipMaxJaroScore') || .85;
            nameMaxJaroScore = getCachedConfigField('dropShipNameMaxJaroScore') || .9;

            if (!isDropShipAllowed()) {
                return;
            }

            // If we don't have an address, load the account address into the user
            if (!$scope.user.address) {
                ProfileService.getAccountInfo().then(function(response) {
                    const addr = response?.data?.account?.addresses?.local;
                    if (addr) {
                        $scope.user.address = addr;
                        if (!$scope.user?.phone?.mobile) {
                            // Set the user phone number if we don't have it
                            $scope.user.phone = response?.data?.account?.phoneNumbers;
                        }
                        UserService.setUser($scope.user);
                        publicMethods.checkDropShip();
                    }
                }, function(err) {
                    console.log('Failed to get the account address for the user');
                    console.log(err);
                });
            }

            $scope.$watch('order.dropShip', () => {
                // Keep the dropship flag in sync with localstorage
                OrderManager.saveOrders();
            });
        },

        checkDropShip: function() {
            const name = `${$scope.user.firstName} ${$scope.user.lastName}`;
            // If the email matches, the distributor is shipping to themselves
            if (
                !isDropShipAllowed() ||
                !$scope.order?.selectedAddress ||
                $scope.user?.email.toLowerCase() === $scope.order?.selectedAddress?.shippingEmail.toLowerCase() ||
                phoneNumbersMatch($scope.user?.phone?.mobile?.value, $scope.order?.selectedAddress?.shippingMobilePhone) ||
                namesMatch(name, $scope.order?.selectedAddress?.shippingAddressName) ||
                addressesMatch(accountAddressToString($scope.user?.address), shipAddressToString($scope.order?.selectedAddress))
            ) {
                $scope.showDropShip = false;
            } else {
                $scope.showDropShip = true;
            }
            // Clear the drop ship flag if we no longer show the checkbox
            if (!$scope.showDropShip) {
                $scope.order.dropShip = false;
            }
        }
    };
    // ---------------------------------------------
    //
    //        Private Methods
    //
    // ---------------------------------------------
    function jaroWinkler(s1, s2) {
        function jaro(s1, s2) {
            if (s1 === s2) return 1.0;

            const len1 = s1.length; const len2 = s2.length;
            if (len1 === 0 || len2 === 0) return 0.0;

            const matchDistance = Math.floor(Math.max(len1, len2) / 2) - 1;
            const matches1 = new Array(len1).fill(false);
            const matches2 = new Array(len2).fill(false);
            let matches = 0; let transpositions = 0;

            for (let i = 0; i < len1; i++) {
                const start = Math.max(0, i - matchDistance);
                const end = Math.min(len2 - 1, i + matchDistance);

                for (let j = start; j <= end; j++) {
                    if (matches2[j]) continue;
                    if (s1[i] !== s2[j]) continue;
                    matches1[i] = matches2[j] = true;
                    matches++;
                    break;
                }
            }

            if (matches === 0) return 0.0;

            let k = 0;
            for (let i = 0; i < len1; i++) {
                if (!matches1[i]) continue;
                while (!matches2[k]) k++;
                if (s1[i] !== s2[k]) transpositions++;
                k++;
            }

            return ((matches / len1) + (matches / len2) + ((matches - transpositions / 2) / matches)) / 3.0;
        }

        const jaroScore = jaro(s1, s2);
        let prefixLength = 0;
        for (let i = 0; i < Math.min(s1.length, s2.length, 4); i++) {
            if (s1[i] === s2[i]) prefixLength++;
            else break;
        }

        return jaroScore + (prefixLength * 0.1 * (1 - jaroScore));
    }

    function accountAddressToString(address) {
        if (!address) return '';
        return [
            address.line1,
            address.line2,
            address.line3,
            address.city,
            address.district,
            address.region,
            address.postalCode,
            address.country
        ].filter(Boolean).join(' ');
        // filter(Boolean) only includes values that exist
    }

    function shipAddressToString(address) {
        if (!address) return '';
        return [
            address.shippingAddress1,
            address.shippingAddress2,
            address.shippingCity,
            address.shippingCounty,
            address.shippingState,
            address.shippingPostalCode,
            address.shippingCountry
        ].filter(Boolean).join(' ');
        // filter(Boolean) only includes values that exist
    }

    function normalizeAddress(address) {
        return address?.toLowerCase()
            ?.replace(/[.#,]/g, '') // Remove special characters
            ?.replace(/\b(n)\.?\b/g, 'north')
            ?.replace(/\b(s)\.?\b/g, 'south')
            ?.replace(/\b(e)\.?\b/g, 'east')
            ?.replace(/\b(w)\.?\b/g, 'west')
            ?.replace(/\b(st)\.?\b/g, 'street')
            ?.replace(/\b(ave)\.?\b/g, 'avenue')
            ?.replace(/\b(blvd)\.?\b/g, 'boulevard')
            ?.replace(/\b(rd)\.?\b/g, 'road')
            ?.replace(/\b(cir)\.?\b/g, 'circle')
            ?.replace(/\b(ste)\.?\b/g, 'suite')
            ?.replace(/\b(apt)\.?\b/g, 'apartment')
            ?.replace(/\s+/g, ' ')?.trim();
    }

    function normalizeName(name) {
        return name?.toLowerCase()?.replace(/\s+/g, ' ')?.trim();
    }

    function normalizePhone(phone) {
        return phone?.replace('-', '')?.replace('(', '')?.replace(')', '')?.replace(' ', '')?.trim();
    }

    function phoneNumbersMatch(phone1, phone2) {
        if (!phone1 || !phone2) {
            return false;
        }
        return normalizePhone(phone1) == normalizePhone(phone2);
    }

    function namesMatch(name1, name2) {
        if (!name1 || !name2) {
            return false;
        }
        const reversedName1 = name1?.split(' ')?.reverse()?.join(' ');
        return (jaroWinkler(normalizeName(name1), normalizeName(name2)) > nameMaxJaroScore) ||
            (jaroWinkler(normalizeName(reversedName1), normalizeName(name2)) > nameMaxJaroScore);
    }

    function addressesMatch(addr1, addr2) {
        if (!addr1 || !addr2) {
            return false;
        }
        return jaroWinkler(normalizeAddress(addr1), normalizeAddress(addr2)) > maxJaroScore;
    }

    /**
     * Drop Ship is not allowed for adr or adroverride
     * Check for drop ship configuration to be enabled
     * Must have a user
     * User must be shopping in their country
     * Only allowed for distributors
     * @return boolean
     */
    function isDropShipAllowed() {
        return !$scope.order?.adr &&
            !$scope.adr &&
            !$scope.overriddenADR &&
            getCachedConfigField('enableDropShip') &&
            $scope.user?.isDistributor() &&
            !isNLXX();
    }

    function isNLXX() {
        return $scope.runConfig.country == 'NL' && $scope.order?.selectedAddress?.shippingCountry !== 'NL';
    }

    return publicMethods;
}();

export default DropShipUtil;
